import React, { useEffect, useState, useMemo } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { calculateFamilyAssignment, calculateIndemnizacion, formatCurrency, getRegimeName } from '../lib/payrollEngine';
import type { Employee, VacationBalance } from '../lib/supabase';

// ── Tipos ─────────────────────────────────────────────────────────────────────
type DismissalType = 'arbitrario' | 'renuncia' | 'mutuo_disenso' | 'jubilacion' | 'culminacion_contrato';

interface MonthInfo {
  key: string;        // 'YYYY-MM'
  label: string;      // 'Mar 25'
  fullLabel: string;  // 'Marzo 2025'
  year: number;
  month: number;      // 0-indexed
  isComplete: boolean;
  isFirst: boolean;
  isLast: boolean;
}

interface AttendanceRec {
  date: string;
  status: string;
}

// ── Helpers visualización ─────────────────────────────────────────────────────
const MONTHS_S = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
const MONTHS_F = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function getMonthTimeline(hireDate: string, dismissalDate: string): MonthInfo[] {
  const hire     = new Date(hireDate     + 'T12:00:00');
  const dismissal= new Date(dismissalDate+ 'T12:00:00');
  const list: MonthInfo[] = [];
  let cur = new Date(hire.getFullYear(), hire.getMonth(), 1);
  const end = new Date(dismissal.getFullYear(), dismissal.getMonth(), 1);
  while (cur <= end) {
    const y = cur.getFullYear(), m = cur.getMonth();
    const last  = new Date(y, m + 1, 0);
    list.push({
      key: `${y}-${String(m + 1).padStart(2, '0')}`,
      label: `${MONTHS_S[m]} ${String(y).slice(2)}`,
      fullLabel: `${MONTHS_F[m]} ${y}`,
      year: y, month: m,
      isComplete: hireDate <= `${y}-${String(m + 1).padStart(2, '0')}-01` && dismissalDate >= `${y}-${String(m + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`,
      isFirst: y === hire.getFullYear() && m === hire.getMonth(),
      isLast:  y === dismissal.getFullYear() && m === dismissal.getMonth(),
    });
    cur.setMonth(cur.getMonth() + 1);
  }
  return list;
}

function getCalendarGrid(year: number, month: number): (number | null)[] {
  const first = new Date(year, month, 1);
  const last  = new Date(year, month + 1, 0);
  const dow   = (first.getDay() + 6) % 7; // 0=Lun
  const grid: (number | null)[] = Array(dow).fill(null);
  for (let d = 1; d <= last.getDate(); d++) grid.push(d);
  while (grid.length % 7 !== 0) grid.push(null);
  return grid;
}

// ── Componente: cuadro de mes ─────────────────────────────────────────────────
function MonthSquare({ info, records, onClick, delay }: {
  info: MonthInfo; records: AttendanceRec[]; onClick: () => void; delay: number;
}) {
  const worked = records.filter(r => r.status !== 'absent').length;
  const bg = info.isComplete
    ? 'bg-primary text-white shadow-md shadow-primary/20'
    : info.isFirst || info.isLast
    ? 'bg-[#00182A]/10 text-[#00182A] border-2 border-[#00182A]/20'
    : 'bg-surface-container text-on-surface border border-outline-variant/30';
  return (
    <button
      onClick={onClick}
      title={info.fullLabel}
      className={`relative flex flex-col items-center justify-center w-14 h-14 rounded-xl font-bold cursor-pointer transition-all hover:scale-110 active:scale-95 ${bg}`}
      style={{ animation: `fadeIn 0.35s ease-out ${delay}ms both` }}
    >
      <span className="text-[11px] font-black leading-tight">{info.label.split(' ')[0]}</span>
      <span className="text-[9px] opacity-60 leading-tight">{info.label.split(' ')[1]}</span>
      {info.isComplete && (
        <span className="material-symbols-outlined text-[10px] mt-0.5" style={{ fontVariationSettings: "'FILL' 1" }}>check</span>
      )}
      {records.length > 0 && (
        <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white border border-primary/30 shadow text-[8px] font-black text-[#00182A] flex items-center justify-center">
          {worked}
        </span>
      )}
    </button>
  );
}

// ── Componente: modal calendario ──────────────────────────────────────────────
function CalendarModal({ info, records, onClose }: {
  info: MonthInfo; records: AttendanceRec[]; onClose: () => void;
}) {
  const grid = getCalendarGrid(info.year, info.month);
  const byDay: Record<number, AttendanceRec> = {};
  records.forEach(r => { byDay[parseInt(r.date.slice(8), 10)] = r; });

  function dayCls(status?: string) {
    switch (status) {
      case 'present':    return 'bg-green-100  text-green-800  border-green-200';
      case 'late':       return 'bg-amber-100  text-amber-800  border-amber-200';
      case 'absent':     return 'bg-red-100    text-red-800    border-red-200';
      case 'vacation':   return 'bg-blue-100   text-blue-800   border-blue-200';
      case 'sick_leave': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'permission': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default:           return 'bg-surface-container-low text-on-surface-variant/40 border-transparent';
    }
  }
  function dayLbl(status?: string) {
    switch (status) {
      case 'present':    return 'P';
      case 'late':       return 'T';
      case 'absent':     return 'F';
      case 'vacation':   return 'V';
      case 'sick_leave': return 'E';
      case 'permission': return 'Pe';
      default:           return '';
    }
  }

  const counts = {
    present:  records.filter(r => r.status === 'present').length,
    late:     records.filter(r => r.status === 'late').length,
    absent:   records.filter(r => r.status === 'absent').length,
    vacation: records.filter(r => r.status === 'vacation').length,
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xs p-5 animate-fade-in" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-headline font-bold text-on-surface text-base">{info.fullLabel}</h3>
            <span className={`text-xs font-semibold ${info.isComplete ? 'text-primary' : 'text-on-surface-variant'}`}>
              {info.isComplete ? '✓ Mes completo' : info.isFirst ? 'Mes de ingreso' : info.isLast ? 'Mes de cese' : ''}
            </span>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-surface-container text-on-surface-variant">
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        {/* Cabecera días */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {['L','M','M','J','V','S','D'].map((d, i) => (
            <div key={i} className={`text-center text-[9px] font-black py-0.5 ${i >= 5 ? 'text-on-surface-variant/30' : 'text-on-surface-variant'}`}>{d}</div>
          ))}
        </div>

        {/* Grilla del calendario */}
        <div className="grid grid-cols-7 gap-1 mb-4">
          {grid.map((day, i) => {
            if (!day) return <div key={i} />;
            const isWknd = i % 7 >= 5;
            const rec = byDay[day];
            return (
              <div key={i} className={`aspect-square rounded-lg border flex flex-col items-center justify-center text-[10px] font-bold
                ${isWknd
                  ? 'bg-surface-container-low/30 text-on-surface-variant/25 border-transparent'
                  : rec ? dayCls(rec.status) : 'bg-surface-container-low/60 text-on-surface-variant/40 border-transparent'}`}>
                <span>{day}</span>
                {rec && !isWknd && <span className="text-[7px] font-black opacity-75 leading-none">{dayLbl(rec.status)}</span>}
              </div>
            );
          })}
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-4 gap-1">
          {[
            { label: 'Presente', count: counts.present,  color: 'bg-green-50  text-green-700'  },
            { label: 'Tardanza', count: counts.late,     color: 'bg-amber-50  text-amber-700'  },
            { label: 'Falta',    count: counts.absent,   color: 'bg-red-50    text-red-700'    },
            { label: 'Vacac.',   count: counts.vacation, color: 'bg-blue-50   text-blue-700'   },
          ].map(s => (
            <div key={s.label} className={`rounded-lg p-1.5 text-center ${s.color}`}>
              <p className="text-sm font-black">{s.count}</p>
              <p className="text-[9px] font-semibold leading-tight">{s.label}</p>
            </div>
          ))}
        </div>
        {records.length === 0 && (
          <p className="text-center text-xs text-on-surface-variant mt-3">Sin registros de asistencia</p>
        )}
      </div>
    </div>
  );
}

interface LiquidationResult {
  // Conceptos (la remuneración del mes ya fue pagada por planilla)
  vacacionesTruncas:     number;
  vacDaysOwed:           number;
  vacMonths:             number;
  vacExtraDays:          number;
  ctsTrunca:             number;
  ctsMonths:             number;
  ctsDays:               number;
  gratificacionTrunca:   number;
  gratifSemesterMonths:  number;
  bonifExtraTrunca:      number;
  indemnizacion:         number;
  indemnYears:           number;
  indemnMonths:          number;
  totalBruto:            number;
  // Descuentos empleado
  pensionDeduction:      number;
  incomeTaxApprox:       number;
  totalDeductions:       number;
  netPayable:            number;
  // Costo empleador adicional
  essaludAmount:         number;
  // Meta
  rc:                    number;
  rcCts:                 number;
  familyAssignment:      number;
}

// ── Helpers de fecha ──────────────────────────────────────────────────────────
function diffMonthsDays(fromStr: string, toStr: string): { months: number; days: number } {
  const from = new Date(fromStr + 'T12:00:00');
  const to   = new Date(toStr   + 'T12:00:00');
  let months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
  let days   = to.getDate() - from.getDate();
  if (days < 0) {
    months--;
    days += new Date(to.getFullYear(), to.getMonth(), 0).getDate();
  }
  // Si el período inició el día 1 y el cese es el último día del mes → mes completo
  // (aplica a períodos CTS y gratificación que siempre arrancan el día 1)
  const lastDayOfToMonth = new Date(to.getFullYear(), to.getMonth() + 1, 0).getDate();
  if (from.getDate() === 1 && to.getDate() === lastDayOfToMonth && days > 0) {
    months++;
    days = 0;
  }
  return { months: Math.max(0, months), days: Math.max(0, days) };
}

function clamp(v: number, min = 0, max = Infinity) {
  return Math.min(Math.max(v, min), max);
}

// ── Lógica de cálculo ─────────────────────────────────────────────────────────
function calculateLiquidation(
  employee: Employee,
  company: any,
  dismissalDateStr: string,
  dismissalType: DismissalType,
  vacBalance: VacationBalance | null,
): LiquidationResult {
  const hire       = employee.hire_date;
  const dismissal  = new Date(dismissalDateStr + 'T12:00:00');
  const regime     = company.regime as string;
  const uit        = company.uit ?? 5500;

  const familyAssignment = calculateFamilyAssignment(employee, company);
  const rc         = employee.base_salary + familyAssignment;
  const rcCts      = rc + employee.base_salary / 6;

  const regimeFactor   = regime === 'general' ? 1.0 : regime === 'pequeña_empresa' ? 0.5 : 0.5;
  const vacDaysPerYear = regime === 'general' ? 30 : 15;

  // ── 1. Vacaciones truncas ─────────────────────────────────────────────────
  // Período: desde el inicio del año vacacional actual (aniversario de ingreso) hasta cese.
  // Aniversario = mes y día de hire_date en el año del cese.
  const hireDate = new Date(hire + 'T12:00:00');
  const annivThisYear = new Date(dismissal.getFullYear(), hireDate.getMonth(), hireDate.getDate());
  const vacPeriodStart = annivThisYear <= dismissal
    ? annivThisYear
    : new Date(dismissal.getFullYear() - 1, hireDate.getMonth(), hireDate.getDate());

  const vacPeriodStartStr = vacPeriodStart.toISOString().slice(0, 10);
  const { months: vacMonths, days: vacExtraDays } = diffMonthsDays(vacPeriodStartStr, dismissalDateStr);

  let vacDaysOwed: number;
  let vacacionesTruncas: number;

  if (regime === 'microempresa') {
    // Microempresa: sueldo/24 × meses completos + sueldo/(24×30) × días adicionales
    vacacionesTruncas = Math.round(
      ((rc / 24) * vacMonths + (rc / (24 * 30)) * vacExtraDays) * 100
    ) / 100;
    vacDaysOwed = vacMonths + vacExtraDays / 30; // solo para referencia en UI
  } else {
    const vacDaysProportional = (vacMonths / 12 + vacExtraDays / 360) * vacDaysPerYear * regimeFactor;
    const daysTaken = vacBalance?.days_taken ?? 0;
    vacDaysOwed = Math.max(0, vacDaysProportional - daysTaken);
    vacacionesTruncas = Math.round((rc / 30) * vacDaysOwed * 100) / 100;
  }

  // ── 3. CTS trunca ────────────────────────────────────────────────────────
  // Períodos CTS: Nov–Abr (depósito mayo) y May–Oct (depósito nov)
  const dismissalMonth = dismissal.getMonth() + 1; // 1-12
  let ctsPeriodStart: string;
  if (dismissalMonth >= 11 || dismissalMonth <= 4) {
    // Período Nov–Abr
    const startYear = dismissalMonth >= 11 ? dismissal.getFullYear() : dismissal.getFullYear() - 1;
    ctsPeriodStart = `${startYear}-11-01`;
  } else {
    // Período May–Oct
    ctsPeriodStart = `${dismissal.getFullYear()}-05-01`;
  }
  const { months: ctsMonths, days: ctsDays } = diffMonthsDays(ctsPeriodStart, dismissalDateStr);
  const ctsRegimeFactor = regime === 'general' ? 1.0 : regime === 'pequeña_empresa' ? 0.5 : 0.0;
  const ctsTrunca = Math.round(
    ((rcCts / 12) * ctsMonths + (rcCts / 360) * ctsDays) * ctsRegimeFactor * 100
  ) / 100;

  // ── 4. Gratificación trunca ───────────────────────────────────────────────
  // Semestres: Ene–Jun (pago jul) y Jul–Dic (pago dic)
  let gratifPeriodStart: string;
  if (dismissalMonth >= 1 && dismissalMonth <= 6) {
    gratifPeriodStart = `${dismissal.getFullYear()}-01-01`;
  } else {
    gratifPeriodStart = `${dismissal.getFullYear()}-07-01`;
  }
  const { months: gratifMonthsRaw, days: gratifDaysRaw } = diffMonthsDays(gratifPeriodStart, dismissalDateStr);
  // Gratificación solo cuenta meses completos (no días)
  const gratifSemesterMonths = clamp(gratifMonthsRaw + (gratifDaysRaw >= 15 ? 0.5 : 0), 0, 6);
  const gratifRegimeFactor   = regime === 'general' ? 1.0 : regime === 'pequeña_empresa' ? 0.5 : 0.0;
  const gratificacionTrunca  = Math.round(rc * (gratifSemesterMonths / 6) * gratifRegimeFactor * 100) / 100;

  // ── 5. Bonificación extraordinaria trunca (9%) ────────────────────────────
  const bonifExtraTrunca = Math.round(gratificacionTrunca * 0.09 * 100) / 100;

  // ── 6. Indemnización ─────────────────────────────────────────────────────
  let indemnizacion = 0, indemnYears = 0, indemnMonths = 0;
  if (dismissalType === 'arbitrario') {
    const result = calculateIndemnizacion(
      employee.base_salary, familyAssignment, regime, hire, dismissalDateStr
    );
    indemnizacion = result.amount;
    indemnYears   = result.years;
    indemnMonths  = result.months;
  }

  // ── 6b. Total bruto ───────────────────────────────────────────────────────
  const totalBruto = Math.round(
    (vacacionesTruncas + ctsTrunca + gratificacionTrunca + bonifExtraTrunca + indemnizacion) * 100
  ) / 100;

  // ── 7. Descuentos del empleado ────────────────────────────────────────────
  // AFP/ONP afecta solo vacaciones truncas (la rem. del mes ya se descontó en planilla)
  const pensionSystem = (employee as any).pension_system ?? company.afp_system ?? 'onp';
  const pensionRate   = (employee as any).pension_rate   ?? company.snp_rate   ?? 13;
  const pensionDeduction = pensionSystem !== 'ninguno'
    ? Math.round(vacacionesTruncas * (pensionRate / 100) * 100) / 100
    : 0;

  // 5ta categoría: vac. truncas + gratif. trunca + bonif. extra (CTS e indemn. NO afectos)
  const taxableBase5ta = vacacionesTruncas + gratificacionTrunca + bonifExtraTrunca;
  const annualGross5ta = taxableBase5ta + (employee.base_salary + familyAssignment) * 11;
  const incomeTaxApprox = (() => {
    const taxableIncome = annualGross5ta - (7 * uit);
    if (taxableIncome <= 0) return 0;
    const brackets = [
      { limit: 5  * uit, rate: 0.08 },
      { limit: 20 * uit, rate: 0.14 },
      { limit: 35 * uit, rate: 0.17 },
      { limit: 45 * uit, rate: 0.20 },
      { limit: Infinity, rate: 0.30 },
    ];
    let tax = 0, rem = taxableIncome, prev = 0;
    for (const b of brackets) {
      const bucket = Math.min(rem, b.limit - prev);
      if (bucket <= 0) break;
      tax += bucket * b.rate;
      rem -= bucket;
      prev = b.limit;
      if (rem <= 0) break;
    }
    return Math.round((tax / 12) * 100) / 100;
  })();

  const totalDeductions = pensionDeduction + incomeTaxApprox;
  const netPayable      = Math.round((totalBruto - totalDeductions) * 100) / 100;

  // ── 8. EsSalud (costo empleador) ─────────────────────────────────────────
  const essaludAmount = Math.round(vacacionesTruncas * (company.essalud_rate / 100) * 100) / 100;

  return {
    vacacionesTruncas, vacDaysOwed, vacMonths, vacExtraDays,
    ctsTrunca, ctsMonths, ctsDays,
    gratificacionTrunca, gratifSemesterMonths, bonifExtraTrunca,
    indemnizacion, indemnYears, indemnMonths,
    totalBruto, pensionDeduction, incomeTaxApprox, totalDeductions,
    netPayable, essaludAmount, rc, rcCts, familyAssignment,
  };
}

// ── Generación de PDF ─────────────────────────────────────────────────────────
function generateLiquidacionPDF(
  emp: Employee,
  company: any,
  dismissalDateStr: string,
  dismissalType: DismissalType,
  result: LiquidationResult,
) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const PAGE_W = 210;
  const PAGE_H = 297;
  const ML = 22;           // margen izquierdo
  const MR = 22;           // margen derecho
  const CW = PAGE_W - ML - MR; // ancho de contenido

  // ── Helpers ───────────────────────────────────────────────────────────────
  const sectionTitle = (text: string, yPos: number) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(text, ML, yPos);
    return yPos + 7;
  };

  const subTitle = (text: string, yPos: number) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(text, ML, yPos);
    return yPos + 6;
  };

  const normalLine = (text: string, yPos: number, indent = 4) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(text, ML + indent, yPos);
    return yPos + 5.5;
  };

  const boldLine = (text: string, yPos: number, indent = 4) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(text, ML + indent, yPos);
    doc.setFont('helvetica', 'normal');
    return yPos + 5.5;
  };

  const divider = (yPos: number, gap = 6) => {
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.line(ML, yPos, PAGE_W - MR, yPos);
    return yPos + gap;
  };

  const autoTable = (startY: number, head: string[][], body: string[][], lastBold = true) => {
    (doc as any).autoTable({
      startY,
      head,
      body,
      margin: { left: ML, right: MR },
      styles: { fontSize: 10, cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 } },
      headStyles: {
        fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold',
        lineWidth: { top: 0.4, bottom: 0.4, left: 0, right: 0 }, lineColor: [140, 140, 140],
      },
      bodyStyles: { lineWidth: 0, fillColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      columnStyles: {
        0: { cellWidth: CW * 0.72 },
        1: { cellWidth: CW * 0.28, halign: 'right' as const },
      },
      didParseCell: (data: any) => {
        if (lastBold && data.section === 'body' && data.row.index === body.length - 1)
          data.cell.styles.fontStyle = 'bold';
      },
      didDrawCell: (data: any) => {
        // línea inferior en cada fila
        doc.setDrawColor(210, 210, 210);
        doc.setLineWidth(0.2);
        doc.line(data.cell.x, data.cell.y + data.cell.height,
          data.cell.x + data.cell.width, data.cell.y + data.cell.height);
      },
    });
    return (doc as any).lastAutoTable.finalY;
  };

  // ── Tiempo de servicio ────────────────────────────────────────────────────
  const hire = new Date(emp.hire_date + 'T12:00:00');
  const dismiss = new Date(dismissalDateStr + 'T12:00:00');
  const totalM = (dismiss.getFullYear() - hire.getFullYear()) * 12 + (dismiss.getMonth() - hire.getMonth());
  const svcYears = Math.floor(totalM / 12);
  const svcMonths = totalM % 12;
  const svcStr = svcYears > 0
    ? `${svcYears} año${svcYears !== 1 ? 's' : ''} y ${svcMonths} mes${svcMonths !== 1 ? 'es' : ''}`
    : `${svcMonths} mes${svcMonths !== 1 ? 'es' : ''}`;
  const dismissLabel = DISMISSAL_TYPES_PDF.find(t => t.value === dismissalType)?.label ?? dismissalType;
  const isAFP = (emp as any).pension_system === 'afp';
  const regime = company.regime as string;

  // ══════════════════════════════════════════════════════════════════════════
  // PÁGINA 1  —  Secciones 1 a 4
  // ══════════════════════════════════════════════════════════════════════════
  let y = 22;

  // ── Título ────────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  const titleText = 'LIQUIDACIÓN DE BENEFICIOS SOCIALES';
  doc.text(titleText, PAGE_W / 2, y, { align: 'center' });
  const tw = doc.getTextWidth(titleText);
  doc.setDrawColor(0);
  doc.setLineWidth(0.4);
  doc.line(PAGE_W / 2 - tw / 2, y + 1.5, PAGE_W / 2 + tw / 2, y + 1.5);
  y += 12;

  // ── Empresa ───────────────────────────────────────────────────────────────
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');   doc.text('EMPRESA:', ML, y);
  doc.setFont('helvetica', 'normal'); doc.text((company.name ?? '').toUpperCase(), ML + 24, y);
  y += 6;
  doc.setFont('helvetica', 'bold');   doc.text('RUC:', ML, y);
  doc.setFont('helvetica', 'normal'); doc.text(company.ruc ?? '', ML + 14, y);
  y += 10;
  y = divider(y, 8);

  // ── 1. Datos del trabajador ───────────────────────────────────────────────
  y = sectionTitle('1. DATOS DEL TRABAJADOR', y);
  const COL2 = ML + 54;
  const pairs: [string, string][] = [
    ['Nombre:',               `${emp.first_name} ${emp.last_name}`],
    ['Fecha de ingreso:',     emp.hire_date],
    ['Fecha de cese:',        dismissalDateStr],
    ['Tipo de cese:',         dismissLabel],
    ['Tiempo de servicios:',  svcStr],
    ['Remuneración mensual:', `S/ ${result.rc.toFixed(2)}`],
    ['Régimen laboral:',      getRegimeName(regime)],
  ];
  doc.setFontSize(10);
  for (const [label, value] of pairs) {
    doc.setFont('helvetica', 'bold');   doc.text(label, ML, y);
    doc.setFont('helvetica', 'normal'); doc.text(value, COL2, y);
    y += 6;
  }
  y += 3;
  y = divider(y, 8);

  // ── 2. Cálculo de beneficios ──────────────────────────────────────────────
  y = sectionTitle('2. CÁLCULO DE BENEFICIOS', y);

  // A. Remuneración pendiente
  y = subTitle('A. Remuneración pendiente', y);
  y = normalLine('No cuenta con remuneraciones pendientes de pago a la fecha.', y);
  y += 4;

  // B. Vacaciones truncas
  let letter = 'B';
  y = subTitle(`${letter}. Vacaciones truncas`, y);
  if (regime === 'microempresa') {
    const tStr = `${result.vacMonths} mes${result.vacMonths !== 1 ? 'es' : ''}` +
      (result.vacExtraDays > 0 ? ` y ${result.vacExtraDays} día${result.vacExtraDays !== 1 ? 's' : ''}` : '');
    y = normalLine(`Tiempo computable: ${tStr}`, y);
    y = normalLine(`Vacaciones generadas: S/ ${result.vacacionesTruncas.toFixed(2)}`, y);
    y = normalLine(`Vacaciones gozadas : S/ 0.00`, y);
    y += 3;
    y = boldLine(`Vacaciones truncas a pagar: S/ ${result.vacacionesTruncas.toFixed(2)}`, y);
  } else {
    const tStr = `${result.vacMonths} mes${result.vacMonths !== 1 ? 'es' : ''}` +
      (result.vacExtraDays > 0 ? ` y ${result.vacExtraDays} días` : '');
    y = normalLine(`Tiempo computable: ${tStr}`, y);
    y = normalLine(`Días proporcionales adeudados: ${result.vacDaysOwed.toFixed(1)} días`, y);
    y = normalLine(`Vacaciones gozadas: 0 días`, y);
    y += 3;
    y = boldLine(`Vacaciones truncas a pagar: S/ ${result.vacacionesTruncas.toFixed(2)}`, y);
  }
  y += 3;
  letter = String.fromCharCode(letter.charCodeAt(0) + 1);

  // C. CTS trunca
  if (result.ctsTrunca > 0) {
    y = subTitle(`${letter}. CTS trunca`, y);
    const cStr = `${result.ctsMonths} mes${result.ctsMonths !== 1 ? 'es' : ''}` +
      (result.ctsDays > 0 ? ` y ${result.ctsDays} días` : '');
    y = normalLine(`Período computable: ${cStr}`, y);
    y = normalLine(`Base de cálculo (RC + 1/6 gratif.): S/ ${result.rcCts.toFixed(2)}`, y);
    y += 3;
    y = boldLine(`CTS trunca a depositar: S/ ${result.ctsTrunca.toFixed(2)}`, y);
    y += 3;
    letter = String.fromCharCode(letter.charCodeAt(0) + 1);
  }

  // D. Gratificación trunca
  if (result.gratificacionTrunca > 0) {
    y = subTitle(`${letter}. Gratificación trunca`, y);
    y = normalLine(`Meses computables del semestre: ${result.gratifSemesterMonths}`, y);
    y = normalLine(`Gratificación trunca: S/ ${result.gratificacionTrunca.toFixed(2)}`, y);
    y = normalLine(`Bonificación extraordinaria (9%): S/ ${result.bonifExtraTrunca.toFixed(2)}`, y);
    y += 3;
    letter = String.fromCharCode(letter.charCodeAt(0) + 1);
  }

  // E. Indemnización
  if (result.indemnizacion > 0) {
    y = subTitle(`${letter}. Indemnización por despido arbitrario`, y);
    const iStr = `${result.indemnYears} año${result.indemnYears !== 1 ? 's' : ''}` +
      (result.indemnMonths > 0 ? ` y ${result.indemnMonths} mes${result.indemnMonths !== 1 ? 'es' : ''}` : '');
    y = normalLine(`Tiempo de servicios: ${iStr}`, y);
    y += 3;
    y = boldLine(`Indemnización: S/ ${result.indemnizacion.toFixed(2)}`, y);
    y += 3;
  }

  y = divider(y, 8);

  // ── 3. Resumen de liquidación ─────────────────────────────────────────────
  y = sectionTitle('3. RESUMEN DE LIQUIDACIÓN', y);
  const summaryBody: string[][] = [
    ['Remuneración pendiente', 'S/ 0.00'],
    ['Vacaciones truncas', `S/ ${result.vacacionesTruncas.toFixed(2)}`],
  ];
  if (result.ctsTrunca > 0)           summaryBody.push(['CTS trunca', `S/ ${result.ctsTrunca.toFixed(2)}`]);
  if (result.gratificacionTrunca > 0) summaryBody.push(['Gratificación trunca', `S/ ${result.gratificacionTrunca.toFixed(2)}`]);
  if (result.bonifExtraTrunca > 0)    summaryBody.push(['Bonificación extraordinaria (9%)', `S/ ${result.bonifExtraTrunca.toFixed(2)}`]);
  if (result.indemnizacion > 0)       summaryBody.push(['Indemnización por despido arbitrario', `S/ ${result.indemnizacion.toFixed(2)}`]);
  summaryBody.push(['Total bruto', `S/ ${result.totalBruto.toFixed(2)}`]);
  y = autoTable(y, [['Concepto', 'Importe']], summaryBody) + 10;

  // ── 4. Descuentos legales ─────────────────────────────────────────────────
  y = sectionTitle('4. DESCUENTOS LEGALES', y);
  const pensionLabel = isAFP ? 'AFP remuneración' : 'ONP (13%)';
  const discBody: string[][] = [[pensionLabel, `S/ ${result.pensionDeduction.toFixed(2)}`]];
  if (result.incomeTaxApprox > 0)
    discBody.push(['Renta 5ta categoría (aprox.)', `S/ ${result.incomeTaxApprox.toFixed(2)}`]);
  discBody.push([`Total descuentos ${isAFP ? 'AFP' : ''}`.trim(), `S/ ${result.totalDeductions.toFixed(2)}`]);
  autoTable(y, [['Concepto', 'Descuento']], discBody);

  // ══════════════════════════════════════════════════════════════════════════
  // PÁGINA 2  —  Sección 5 + declaración + firma
  // ══════════════════════════════════════════════════════════════════════════
  doc.addPage();
  y = 28;

  // ── 5. Monto neto de liquidación ──────────────────────────────────────────
  y = sectionTitle('5. MONTO NETO DE LIQUIDACIÓN', y);
  y += 2;
  y = normalLine(`Total bruto: S/ ${result.totalBruto.toFixed(2)}`, y, 0);
  y = normalLine(`Total descuentos ${isAFP ? 'AFP' : ''}:  S/ ${result.totalDeductions.toFixed(2)}`.trim(), y, 0);
  y += 4;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(`Neto de liquidación: S/ ${result.netPayable.toFixed(2)}`, ML, y);
  doc.setFont('helvetica', 'normal');
  y += 18;

  // ── Declaración ───────────────────────────────────────────────────────────
  doc.setFontSize(10);
  const decl =
    'El trabajador declara haber recibido el monto final señalado en la presente liquidación, ' +
    'no teniendo reclamo alguno que formular por concepto de remuneraciones o beneficios sociales ' +
    'derivados de la relación laboral.';
  const declLines: string[] = doc.splitTextToSize(decl, CW);
  doc.text(declLines, ML, y);
  y += declLines.length * 5.5;

  // ── Firma  (centrada en el tercio inferior de la página) ──────────────────
  const sigY = Math.max(y + 40, PAGE_H - 60);
  const sigX = PAGE_W / 2;
  doc.setDrawColor(0);
  doc.setLineWidth(0.5);
  doc.line(sigX - 40, sigY, sigX + 40, sigY);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Firma del trabajador', sigX, sigY + 7, { align: 'center' });
  doc.setFont('helvetica', 'bold');
  doc.text(`${emp.first_name} ${emp.last_name}`, sigX, sigY + 13, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`DNI: ${(emp as any).document_number ?? ''}`, sigX, sigY + 19, { align: 'center' });

  // ── Número de páginas ─────────────────────────────────────────────────────
  const totalPages = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150);
    doc.text(`Página ${i} de ${totalPages}`, PAGE_W - MR, PAGE_H - 10, { align: 'right' });
    doc.text(`Generado: ${new Date().toLocaleDateString('es-PE')}`, ML, PAGE_H - 10);
    doc.setTextColor(0);
  }

  doc.save(`Liquidacion_${emp.last_name}_${emp.first_name}_${dismissalDateStr}.pdf`);
}

// Labels usados dentro del PDF (no puede acceder a DISMISSAL_TYPES porque se define abajo)
const DISMISSAL_TYPES_PDF = [
  { value: 'arbitrario',           label: 'Despido arbitrario' },
  { value: 'renuncia',             label: 'Renuncia voluntaria' },
  { value: 'culminacion_contrato', label: 'Culminación de contrato' },
  { value: 'mutuo_disenso',        label: 'Mutuo disenso' },
  { value: 'jubilacion',           label: 'Jubilación' },
];

// ── Constantes UI ─────────────────────────────────────────────────────────────
const DISMISSAL_TYPES: { value: DismissalType; label: string; hasIndemn: boolean; note?: string }[] = [
  { value: 'arbitrario',          label: 'Despido arbitrario',       hasIndemn: true,  note: 'Incluye indemnización (D.Leg. 728)' },
  { value: 'renuncia',            label: 'Renuncia voluntaria',      hasIndemn: false, note: 'Sin indemnización' },
  { value: 'culminacion_contrato',label: 'Culminación de contrato',  hasIndemn: false, note: 'Contrato a plazo fijo / por obra — sin indemnización' },
  { value: 'mutuo_disenso',       label: 'Mutuo disenso',            hasIndemn: false, note: 'Sin indemnización' },
  { value: 'jubilacion',          label: 'Jubilación',               hasIndemn: false, note: 'Sin indemnización' },
];

// ── Componente ────────────────────────────────────────────────────────────────
export function LiquidationPage() {
  const { company } = useAuth();
  const [employees, setEmployees]         = useState<Employee[]>([]);
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [dismissalDate, setDismissalDate] = useState('');
  const [dismissalType, setDismissalType] = useState<DismissalType>('arbitrario');
  const [vacBalance, setVacBalance]       = useState<VacationBalance | null>(null);
  const [result, setResult]               = useState<LiquidationResult | null>(null);
  const [loading, setLoading]             = useState(false);
  const [attendanceRecs, setAttendanceRecs] = useState<AttendanceRec[]>([]);
  const [loadingAtt, setLoadingAtt]       = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<MonthInfo | null>(null);

  const selectedEmp = employees.find(e => e.id === selectedEmpId);

  // Construye el timeline de meses y agrupa asistencias por mes
  const monthTimeline = useMemo(() =>
    selectedEmp && dismissalDate
      ? getMonthTimeline(selectedEmp.hire_date, dismissalDate)
      : [],
    [selectedEmp, dismissalDate]);

  const recsByMonth = useMemo(() => {
    const map: Record<string, AttendanceRec[]> = {};
    attendanceRecs.forEach(r => {
      const key = r.date.slice(0, 7);
      if (!map[key]) map[key] = [];
      map[key].push(r);
    });
    return map;
  }, [attendanceRecs]);

  useEffect(() => { if (company) loadEmployees(); }, [company]);

  // Carga asistencia completa cuando cambian empleado + fecha de cese
  useEffect(() => {
    if (!selectedEmp || !dismissalDate) { setAttendanceRecs([]); return; }
    setLoadingAtt(true);
    supabase
      .from('attendance')
      .select('date, status')
      .eq('employee_id', selectedEmp.id)
      .gte('date', selectedEmp.hire_date)
      .lte('date', dismissalDate)
      .order('date')
      .then(({ data }) => {
        setAttendanceRecs((data as AttendanceRec[]) || []);
        setLoadingAtt(false);
      });
  }, [selectedEmp?.id, dismissalDate]);

  async function loadEmployees() {
    const { data } = await supabase
      .from('employees').select('*')
      .eq('company_id', company!.id).eq('active', true).order('last_name');
    setEmployees(data || []);
  }

  async function loadVacBalance(empId: string, year: number) {
    const { data } = await supabase
      .from('vacation_balances').select('*')
      .eq('employee_id', empId).eq('year', year).single();
    setVacBalance(data);
  }

  function handleEmployeeChange(empId: string) {
    setSelectedEmpId(empId);
    setResult(null);
    if (empId && dismissalDate)
      loadVacBalance(empId, new Date(dismissalDate + 'T12:00:00').getFullYear());
  }

  function handleDateChange(date: string) {
    setDismissalDate(date);
    setResult(null);
    if (selectedEmpId && date)
      loadVacBalance(selectedEmpId, new Date(date + 'T12:00:00').getFullYear());
  }

  function handleCalculate() {
    const emp = employees.find(e => e.id === selectedEmpId);
    if (!emp || !dismissalDate || !company) return;
    setLoading(true);
    const r = calculateLiquidation(emp, company, dismissalDate, dismissalType, vacBalance);
    setResult(r);
    setLoading(false);
  }

  function Row({ label, amount, note, highlight = false, deduction = false }: {
    label: string; amount: number; note?: string; highlight?: boolean; deduction?: boolean;
  }) {
    if (amount === 0) return null;
    return (
      <div className={`flex items-center justify-between py-2.5 px-4 rounded-lg ${highlight ? 'bg-primary/5 font-bold' : 'hover:bg-surface-container-low/40'}`}>
        <div>
          <span className={`text-sm ${highlight ? 'text-on-surface font-bold' : 'text-on-surface'}`}>{label}</span>
          {note && <p className="text-[10px] text-on-surface-variant mt-0.5">{note}</p>}
        </div>
        <span className={`text-sm font-bold tabular-nums ${deduction ? 'text-error' : highlight ? 'text-primary' : 'text-on-surface'}`}>
          {deduction ? '−' : ''}{formatCurrency(amount)}
        </span>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-extrabold tracking-tight text-on-surface font-headline">
          Liquidación de Cese
        </h1>
        <p className="text-on-surface-variant mt-1">
          Cálculo de beneficios sociales al término de la relación laboral (D.Leg. 728, 650, 713)
        </p>
      </div>

      {/* Formulario */}
      <div className="card p-7 space-y-5">
        <h2 className="font-bold text-on-surface font-headline">Datos del cese</h2>

        <div className="grid grid-cols-2 gap-5">
          {/* Empleado */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Empleado</label>
            <select
              className="input-base"
              value={selectedEmpId}
              onChange={e => handleEmployeeChange(e.target.value)}
            >
              <option value="">— Seleccionar empleado —</option>
              {employees.map(e => (
                <option key={e.id} value={e.id}>
                  {e.last_name}, {e.first_name} — {e.position}
                </option>
              ))}
            </select>
          </div>

          {/* Fecha de cese */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Fecha de cese</label>
            <input
              type="date"
              className="input-base"
              value={dismissalDate}
              onChange={e => handleDateChange(e.target.value)}
            />
          </div>

          {/* Tipo de cese */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Motivo del cese</label>
            <select className="input-base" value={dismissalType}
              onChange={e => { setDismissalType(e.target.value as DismissalType); setResult(null); }}>
              {DISMISSAL_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

        </div>

        {/* Info del empleado seleccionado */}
        {selectedEmp && (
          <div className="p-4 bg-surface-container-low rounded-xl grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-[10px] text-on-surface-variant uppercase font-bold mb-1">Sueldo base</p>
              <p className="font-bold">{formatCurrency(selectedEmp.base_salary)}</p>
            </div>
            <div>
              <p className="text-[10px] text-on-surface-variant uppercase font-bold mb-1">Régimen</p>
              <p className="font-bold">{getRegimeName(company?.regime ?? 'general')}</p>
            </div>
            <div>
              <p className="text-[10px] text-on-surface-variant uppercase font-bold mb-1">Fecha de ingreso</p>
              <p className="font-bold">{selectedEmp.hire_date}</p>
            </div>
          </div>
        )}

        {(() => {
          const t = DISMISSAL_TYPES.find(t => t.value === dismissalType);
          const colors = dismissalType === 'arbitrario'
            ? 'bg-amber-50 border-amber-200 text-amber-800'
            : 'bg-blue-50 border-blue-200 text-blue-800';
          return t ? (
            <div className={`p-3 border rounded-xl text-xs ${colors}`}>
              <strong>{t.label}:</strong>{' '}{t.note}.
              {dismissalType === 'arbitrario' && ' General: 1.5 sueldos/año (tope 12). Pequeña empresa: 20 días/año (tope 120 días). Microempresa: no aplica (Ley 28015).'}
              {dismissalType === 'culminacion_contrato' && ' El trabajador recibe todos los beneficios proporcionales al tiempo laborado (vacaciones, CTS, gratificación), pero no indemnización por despido.'}
            </div>
          ) : null;
        })()}

        {/* ── Cuadros de meses ── */}
        {monthTimeline.length > 0 && (
          <div className="space-y-3 pt-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">
                Meses de relación laboral
              </span>
              {loadingAtt && (
                <span className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {monthTimeline.map((m, i) => (
                <MonthSquare
                  key={m.key}
                  info={m}
                  records={recsByMonth[m.key] || []}
                  onClick={() => setSelectedMonth(m)}
                  delay={i * 40}
                />
              ))}
            </div>

            {/* Leyenda */}
            <div className="flex items-center gap-4 text-[10px] text-on-surface-variant font-medium">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-primary inline-block" /> Mes completo
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded border-2 border-[#00182A]/20 bg-[#00182A]/10 inline-block" /> Parcial
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3.5 h-3.5 rounded-full bg-white border border-primary/30 text-[7px] font-black text-[#00182A] inline-flex items-center justify-center">N</span>
                Días laborados
              </span>
            </div>
          </div>
        )}

        {/* ── Panel de confirmación ── */}
        {selectedEmp && dismissalDate && (
          <div className="bg-surface-container-low rounded-xl p-5 border border-outline-variant/20 space-y-4 animate-fade-in">
            <div className="flex items-center gap-2 mb-1">
              <span className="material-symbols-outlined text-primary text-[18px]">fact_check</span>
              <h3 className="text-sm font-bold text-on-surface">Confirma los datos antes de calcular</h3>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              {/* Fecha ingreso */}
              <div className="bg-white rounded-lg p-3 border border-outline-variant/10">
                <p className="text-[10px] text-on-surface-variant uppercase font-bold mb-1">Fecha de ingreso</p>
                <p className="font-bold text-on-surface">{selectedEmp.hire_date}</p>
              </div>
              {/* Fecha cese editable */}
              <div className="bg-white rounded-lg p-3 border border-outline-variant/10">
                <p className="text-[10px] text-on-surface-variant uppercase font-bold mb-1">Fecha de cese</p>
                <input
                  type="date"
                  value={dismissalDate}
                  onChange={e => handleDateChange(e.target.value)}
                  className="font-bold text-on-surface bg-transparent border-none outline-none w-full text-sm"
                />
              </div>
              {/* Sueldo */}
              <div className="bg-white rounded-lg p-3 border border-outline-variant/10">
                <p className="text-[10px] text-on-surface-variant uppercase font-bold mb-1">Sueldo base</p>
                <p className="font-bold text-on-surface">{formatCurrency(selectedEmp.base_salary)}</p>
              </div>
              {/* Meses completos */}
              <div className="bg-white rounded-lg p-3 border border-outline-variant/10">
                <p className="text-[10px] text-on-surface-variant uppercase font-bold mb-1">Meses completos</p>
                <p className="font-bold text-primary">
                  {monthTimeline.filter(m => m.isComplete).length} de {monthTimeline.length} mes{monthTimeline.length !== 1 ? 'es' : ''}
                </p>
              </div>
            </div>

            <p className="text-[10px] text-on-surface-variant">
              Puedes modificar la fecha de cese directamente arriba si hubo un error. Los cuadros de meses se actualizarán automáticamente.
            </p>
          </div>
        )}

        <button
          onClick={handleCalculate}
          disabled={!selectedEmpId || !dismissalDate || loading}
          className="btn-primary text-white px-8 py-3 rounded-xl font-bold flex items-center gap-2 disabled:opacity-50"
        >
          <span className="material-symbols-outlined text-[20px]">calculate</span>
          Calcular liquidación
        </button>
      </div>

      {/* Modal calendario */}
      {selectedMonth && (
        <CalendarModal
          info={selectedMonth}
          records={recsByMonth[selectedMonth.key] || []}
          onClose={() => setSelectedMonth(null)}
        />
      )}

      {/* Resultado */}
      {result && selectedEmp && (
        <div className="space-y-5">
          {/* Encabezado resumen */}
          <div className="rounded-xl p-6 bg-[#00182A] text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80 font-medium">Liquidación de cese</p>
                <p className="text-2xl font-extrabold font-headline">
                  {selectedEmp.first_name} {selectedEmp.last_name}
                </p>
                <p className="text-sm opacity-80 mt-1">
                  {DISMISSAL_TYPES.find(t => t.value === dismissalType)?.label} · {dismissalDate}
                </p>
              </div>
              <div className="text-right flex flex-col items-end gap-3">
                <div>
                  <p className="text-sm opacity-80">Neto a pagar</p>
                  <p className="text-3xl font-black">{formatCurrency(result.netPayable)}</p>
                </div>
                <button
                  onClick={() => generateLiquidacionPDF(selectedEmp, company, dismissalDate, dismissalType, result)}
                  className="flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 active:bg-white/10 rounded-xl text-sm font-semibold transition-all"
                >
                  <span className="material-symbols-outlined text-[18px]">download</span>
                  Descargar PDF
                </button>
              </div>
            </div>
          </div>

          {/* Detalles */}
          <div className="grid grid-cols-2 gap-5">
            {/* Ingresos */}
            <div className="card p-5 space-y-1">
              <h3 className="font-bold text-on-surface font-headline text-sm mb-3 flex items-center gap-2">
                <span className="material-symbols-outlined text-green-600 text-[18px]">add_circle</span>
                Conceptos a pagar
              </h3>

              <Row
                label="Vacaciones truncas"
                amount={result.vacacionesTruncas}
                note={
                  company?.regime === 'microempresa'
                    ? `${result.vacMonths} mes${result.vacMonths !== 1 ? 'es' : ''} × S/${(result.rc / 24).toFixed(2)}` +
                      (result.vacExtraDays > 0 ? ` + ${result.vacExtraDays} días × S/${(result.rc / 720).toFixed(2)}` : '')
                    : `${result.vacDaysOwed.toFixed(1)} días proporcionales`
                }
              />
              <Row
                label="CTS trunca"
                amount={result.ctsTrunca}
                note={`${result.ctsMonths} mes${result.ctsMonths !== 1 ? 'es' : ''} + ${result.ctsDays} días del período`}
              />
              <Row
                label="Gratificación trunca"
                amount={result.gratificacionTrunca}
                note={`${result.gratifSemesterMonths} mes${result.gratifSemesterMonths !== 1 ? 'es' : ''} del semestre`}
              />
              <Row
                label="Bonificación extraordinaria trunca"
                amount={result.bonifExtraTrunca}
                note="9% de la gratificación trunca (Ley 29351)"
              />
              {result.indemnizacion > 0 && (
                <Row
                  label="Indemnización por despido arbitrario"
                  amount={result.indemnizacion}
                  note={`${result.indemnYears} año${result.indemnYears !== 1 ? 's' : ''} ${result.indemnMonths > 0 ? `+ ${result.indemnMonths} mes${result.indemnMonths !== 1 ? 'es' : ''}` : ''} de servicios`}
                />
              )}

              <div className="border-t border-outline-variant/20 mt-2 pt-2">
                <Row label="Total bruto" amount={result.totalBruto} highlight />
              </div>
            </div>

            {/* Descuentos y neto */}
            <div className="card p-5 space-y-1">
              <h3 className="font-bold text-on-surface font-headline text-sm mb-3 flex items-center gap-2">
                <span className="material-symbols-outlined text-error text-[18px]">remove_circle</span>
                Descuentos del trabajador
              </h3>

              <Row
                label={selectedEmp.pension_system === 'afp' ? 'AFP' : 'ONP (13%)'}
                amount={result.pensionDeduction}
                note="Sobre vacaciones truncas"
                deduction
              />
              <Row
                label="Renta 5ta categoría (aprox.)"
                amount={result.incomeTaxApprox}
                note="Sobre rem. prop. + vac. truncas + gratif. + bonif. extra"
                deduction
              />

              <div className="border-t border-outline-variant/20 mt-2 pt-2">
                <Row label="Total descuentos" amount={result.totalDeductions} deduction />
              </div>

              <div className="border-t-2 border-primary/20 mt-3 pt-3">
                <Row label="NETO A PAGAR AL TRABAJADOR" amount={result.netPayable} highlight />
              </div>

              {/* Costo empleador */}
              <div className="mt-4 p-3 bg-surface-container-low rounded-xl">
                <p className="text-[10px] font-bold text-on-surface-variant uppercase tracking-widest mb-2">
                  Costo adicional empleador
                </p>
                <Row
                  label={`EsSalud (${company?.essalud_rate ?? 9}%)`}
                  amount={result.essaludAmount}
                  note="Sobre rem. proporcional + vacaciones truncas"
                />
              </div>
            </div>
          </div>

          {/* Tabla de afectación */}
          <div className="card overflow-hidden">
            <div className="p-4 border-b border-outline-variant/10">
              <h3 className="font-bold text-on-surface font-headline text-sm">
                Afectación tributaria y previsional por concepto
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-surface-container-low">
                    {['Concepto', 'Monto', 'AFP/ONP', '5ta Cat.', 'EsSalud'].map(h => (
                      <th key={h} className="px-4 py-3 text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/10">
                  {[
                    { label: 'Vacaciones truncas',         amount: result.vacacionesTruncas, afp: true,  imp: true,  ess: true  },
                    { label: 'CTS trunca',                 amount: result.ctsTrunca,         afp: false, imp: false, ess: false },
                    { label: 'Gratificación trunca',       amount: result.gratificacionTrunca, afp: false, imp: true, ess: false },
                    { label: 'Bonif. extraordinaria',      amount: result.bonifExtraTrunca,  afp: false, imp: true,  ess: false },
                    { label: 'Indemnización',               amount: result.indemnizacion,     afp: false, imp: false, ess: false },
                  ].filter(r => r.amount > 0).map(row => (
                    <tr key={row.label} className="hover:bg-surface-container-low/30">
                      <td className="px-4 py-3 font-medium">{row.label}</td>
                      <td className="px-4 py-3 font-bold tabular-nums">{formatCurrency(row.amount)}</td>
                      <td className="px-4 py-3">{row.afp
                        ? <span className="text-xs bg-green-50 text-green-700 font-bold px-2 py-0.5 rounded-full">SÍ</span>
                        : <span className="text-xs bg-surface-container-low text-on-surface-variant px-2 py-0.5 rounded-full">NO</span>}
                      </td>
                      <td className="px-4 py-3">{row.imp
                        ? <span className="text-xs bg-green-50 text-green-700 font-bold px-2 py-0.5 rounded-full">SÍ</span>
                        : <span className="text-xs bg-surface-container-low text-on-surface-variant px-2 py-0.5 rounded-full">NO</span>}
                      </td>
                      <td className="px-4 py-3">{row.ess
                        ? <span className="text-xs bg-blue-50 text-blue-700 font-bold px-2 py-0.5 rounded-full">Empleador</span>
                        : <span className="text-xs bg-surface-container-low text-on-surface-variant px-2 py-0.5 rounded-full">NO</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notas legales */}
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 space-y-1">
            <p><strong>Notas:</strong></p>
            <p>• La 5ta categoría mostrada es una estimación de la retención del mes del cese. El cálculo definitivo debe considerar las retenciones acumuladas del año.</p>
            <p>• La CTS trunca no incluye depósitos ya realizados. Verificar el saldo acumulado en el banco del trabajador.</p>
            <p>• Microempresa (REMYPE): no aplica CTS, gratificación ni indemnización según Ley 28015 y D.Leg. 1086.</p>
          </div>
        </div>
      )}
    </div>
  );
}
